import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminGetWhatsapp, adminSaveWhatsapp, adminWhatsappSendText, adminWhatsappSendTemplate } from "@/lib/api";
import { MessageCircle, Loader2, Save, Send, ShieldCheck, Copy, CheckCircle2 } from "lucide-react";

const inputCls = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL || ""}/api/webhooks/whatsapp`;

export default function AdminWhatsApp() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ phone_number_id: "", waba_id: "", api_version: "v21.0", access_token: "", app_secret: "", webhook_verify_token: "" });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState("");
  const [mode, setMode] = useState("template");
  const [text, setText] = useState("");
  const [tpl, setTpl] = useState({ name: "hello_world", language_code: "en_US", params: "" });

  const load = () => adminGetWhatsapp().then((d) => {
    setCfg(d);
    setForm((f) => ({ ...f, phone_number_id: d.phone_number_id || "", waba_id: d.waba_id || "", api_version: d.api_version || "v21.0" }));
  });
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="py-20 text-center text-ink-3"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { phone_number_id: form.phone_number_id, waba_id: form.waba_id, api_version: form.api_version };
      ["access_token", "app_secret", "webhook_verify_token"].forEach((k) => { if (form[k]) payload[k] = form[k]; });
      const d = await adminSaveWhatsapp(payload);
      setCfg(d);
      setForm((f) => ({ ...f, access_token: "", app_secret: "", webhook_verify_token: "" }));
      toast.success("WhatsApp settings saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!to.trim()) return toast.error("Enter a recipient number (international format).");
    setSending(true);
    try {
      if (mode === "text") {
        const d = await adminWhatsappSendText(to.trim(), text.trim());
        toast.success("Message sent ✓", { description: `id: ${d.result?.messages?.[0]?.id || "sent"}` });
      } else {
        const params = tpl.params.split("|").map((s) => s.trim()).filter(Boolean);
        const d = await adminWhatsappSendTemplate({ to: to.trim(), name: tpl.name.trim(), language_code: tpl.language_code.trim(), body_parameters: params });
        toast.success("Template sent ✓", { description: `id: ${d.result?.messages?.[0]?.id || "sent"}` });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed."); }
    finally { setSending(false); }
  };

  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied."); };

  return (
    <div className="max-w-3xl" data-testid="admin-whatsapp">
      <div className="mb-5 flex items-center gap-3">
        <MessageCircle className="h-6 w-6 text-[#25D366]" />
        <div>
          <h1 className="font-serif-latin text-2xl text-ink">WhatsApp</h1>
          <p className="text-sm text-ink-3">Send notifications & announcements over WhatsApp via Meta's Cloud API. Add your credentials from Meta below.</p>
        </div>
      </div>

      {/* connection status */}
      <div className="mb-5 flex items-center gap-2 text-sm">
        {cfg.configured
          ? <span className="inline-flex items-center gap-1.5 text-green-700" data-testid="wa-status-connected"><CheckCircle2 className="h-4 w-4" /> Connected{cfg.phone_number_id ? ` · phone ID ${cfg.phone_number_id}` : ""}</span>
          : <span className="text-ink-2" data-testid="wa-status-disconnected">Not connected — enter your Meta WhatsApp credentials to begin.</span>}
      </div>

      {/* settings */}
      <div className="space-y-4 border border-line bg-surface p-6">
        <label className="flex items-center gap-3 text-sm text-ink-2">
          <input type="checkbox" data-testid="wa-enabled" checked={!!cfg.enabled} onChange={async (e) => { const d = await adminSaveWhatsapp({ enabled: e.target.checked }); setCfg(d); }} className="h-4 w-4" />
          WhatsApp sending enabled
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">Phone Number ID</label>
            <input data-testid="wa-phone-id" value={form.phone_number_id} onChange={(e) => set("phone_number_id", e.target.value)} className={inputCls} placeholder="123456789012345" />
            <p className="mt-1 text-xs text-ink-3">From Meta → WhatsApp → API Setup (the numeric ID, not the phone number).</p>
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">WhatsApp Business Account ID</label>
            <input data-testid="wa-waba-id" value={form.waba_id} onChange={(e) => set("waba_id", e.target.value)} className={inputCls} placeholder="987654321098765" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">Access token {cfg.token_set && <span className="normal-case text-ink-3">(stored — leave blank to keep)</span>}</label>
            <input data-testid="wa-token" type="password" value={form.access_token} onChange={(e) => set("access_token", e.target.value)} className={inputCls} placeholder={cfg.token_set ? "•••••••• stored" : "Permanent / system-user token"} />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">App secret <span className="normal-case">(optional, for webhook signature)</span></label>
            <input data-testid="wa-app-secret" type="password" value={form.app_secret} onChange={(e) => set("app_secret", e.target.value)} className={inputCls} placeholder={cfg.app_secret_set ? "•••••••• stored" : "Meta app secret"} />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">API version</label>
            <input data-testid="wa-api-version" value={form.api_version} onChange={(e) => set("api_version", e.target.value)} className={inputCls} placeholder="v21.0" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">Webhook verify token <span className="normal-case">(choose any long random string)</span></label>
            <input data-testid="wa-verify-token" value={form.webhook_verify_token} onChange={(e) => set("webhook_verify_token", e.target.value)} className={inputCls} placeholder={cfg.verify_token_set ? "•••••••• stored" : "e.g. dhm_wa_9f4c1e7a2b8d"} />
          </div>
        </div>

        {(cfg.token_set) && <p className="flex items-center gap-2 text-xs text-green-700"><ShieldCheck className="h-4 w-4" /> Credentials are stored securely. Blank secret fields keep the saved value.</p>}

        <div className="rounded border border-line bg-parchment p-3 text-xs text-ink-2">
          <p className="mb-1 font-medium text-ink">Webhook callback URL (set this in Meta → WhatsApp → Configuration):</p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate" data-testid="wa-webhook-url">{WEBHOOK_URL}</code>
            <button onClick={() => copy(WEBHOOK_URL)} className="inline-flex items-center gap-1 text-saffron hover:underline"><Copy className="h-3.5 w-3.5" /> Copy</button>
          </div>
          <p className="mt-1">Use the same <strong>verify token</strong> above when Meta asks for it, then subscribe to the <code>messages</code> field.</p>
        </div>

        <div className="border-t border-line pt-4">
          <button onClick={save} disabled={saving} data-testid="wa-save" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
          </button>
        </div>
      </div>

      {/* send test */}
      <div className="mt-6 border border-line bg-surface p-6">
        <h2 className="mb-1 font-serif-latin text-lg text-ink">Send a test message</h2>
        <p className="mb-4 text-sm text-ink-3">Outside a 24-hour conversation window you must use an approved <strong>template</strong> (e.g. <code>hello_world</code>). Plain text works only after the recipient has messaged you.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">Recipient (international)</label>
            <input data-testid="wa-to" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} placeholder="+919812345678" />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">Message type</label>
            <select data-testid="wa-mode" value={mode} onChange={(e) => setMode(e.target.value)} className={inputCls}>
              <option value="template">Template (recommended)</option>
              <option value="text">Plain text (open session only)</option>
            </select>
          </div>
        </div>
        {mode === "text" ? (
          <textarea data-testid="wa-text" value={text} onChange={(e) => setText(e.target.value)} rows={3} className={`${inputCls} mt-3`} placeholder="Your message…" />
        ) : (
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <input data-testid="wa-tpl-name" value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} className={inputCls} placeholder="template name (e.g. hello_world)" />
            <input data-testid="wa-tpl-lang" value={tpl.language_code} onChange={(e) => setTpl({ ...tpl, language_code: e.target.value })} className={inputCls} placeholder="language (e.g. en_US)" />
            <input data-testid="wa-tpl-params" value={tpl.params} onChange={(e) => setTpl({ ...tpl, params: e.target.value })} className={inputCls} placeholder="body params, | separated" />
          </div>
        )}
        <button onClick={sendTest} disabled={sending} data-testid="wa-send" className="mt-4 inline-flex items-center gap-2 bg-[#25D366] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:brightness-95 disabled:opacity-60">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send WhatsApp message
        </button>
      </div>
    </div>
  );
}
